// src/game-system/races/elf.js
import { BaseRace } from './baseRace';

/**
 * @typedef {Object} ElvenSubrace
 * @property {string} name - Subrace name
 * @property {string} description - Subrace description
 * @property {Object.<string, number>} attributeBonuses - Additional ability score increases
 * @property {Array<RacialTrait>} traits - Subrace-specific traits
 */

/**
 * Base traits shared by all elves
 * @type {Array<RacialTrait>}
 */
const BASE_ELVEN_TRAITS = [
  {
    name: "Keen Senses",
    description: "Your elven heritage grants you supernatural sensory awareness",
    effect: {
      type: "passive",
      mechanics: "Proficiency in the Perception skill"
    }
  },
  {
    name: "Fey Ancestry",
    description: "Your fey heritage protects your mind",
    effect: {
      type: "passive",
      mechanics: "Advantage on saving throws against being charmed, magic can't put you to sleep"
    }
  },
  {
    name: "Trance",
    description: "Elves don't need sleep, instead entering a meditative state",
    effect: {
      type: "passive",
      mechanics: "4 hours of trance equals 8 hours of sleep for other races"
    }
  },
  {
    name: "Darkvision",
    description: "Your elven eyes can see in dark conditions",
    effect: {
      type: "passive",
      mechanics: "You can see in dim light within 60 feet as if it were bright light"
    }
  }
];

/**
 * Elven subrace definitions
 * @type {Object.<string, ElvenSubrace>}
 */
const ELVEN_SUBRACES = {
  high: {
    name: "High Elf",
    description: "Noble elves skilled in magic and swordplay",
    attributeBonuses: {
      intelligence: 1
    },
    traits: [
      {
        name: "Cantrip",
        description: "You know one wizard cantrip of your choice",
        effect: {
          type: "active",
          mechanics: "Cast one wizard cantrip using Intelligence"
        }
      },
      {
        name: "Extra Language",
        description: "You can speak, read, and write one extra language",
        effect: {
          type: "passive",
          mechanics: "Learn one additional language of your choice"
        }
      },
      {
        name: "Elf Weapon Training",
        description: "You gain proficiency with traditional elven weapons",
        effect: {
          type: "passive",
          mechanics: "Proficiency with longsword, shortsword, shortbow, and longbow"
        }
      }
    ]
  },
  wood: {
    name: "Wood Elf",
    description: "Wild elves at home in the forests",
    attributeBonuses: {
      wisdom: 1
    },
    traits: [
      {
        name: "Fleet of Foot",
        description: "Your base walking speed increases",
        effect: {
          type: "passive",
          mechanics: "Base walking speed becomes 35 feet"
        }
      },
      {
        name: "Mask of the Wild",
        description: "You can attempt to hide in natural phenomena",
        effect: {
          type: "active",
          mechanics: "Hide even when only lightly obscured by natural phenomena"
        }
      },
      {
        name: "Wood Elf Weapon Training",
        description: "You gain proficiency with traditional wood elf weapons",
        effect: {
          type: "passive",
          mechanics: "Proficiency with longsword, shortsword, shortbow, and longbow"
        }
      }
    ]
  }
};

/**
 * Elven cultures
 * @type {Array<CulturalBackground>}
 */
const ELVEN_CULTURES = [
  {
    name: "Twilight Court",
    description: "Ancient elven nobility who preserve magical traditions",
    bonusSkills: ["Arcana", "History"],
    startingEquipment: ["Spellbook", "Fine clothes", "Signet ring"],
    traditions: [
      {
        name: "Magical Heritage",
        benefit: "You know one additional cantrip from the wizard spell list"
      }
    ]
  },
  {
    name: "Forest Enclave",
    description: "Secluded woodland communities living in harmony with nature",
    bonusSkills: ["Nature", "Survival"],
    startingEquipment: ["Hunting bow", "Woodland clothing", "Herbalism kit"],
    traditions: [
      {
        name: "Nature's Ally",
        benefit: "Advantage on checks to track creatures or forage for food"
      }
    ]
  }
];

export class Elf extends BaseRace {
  /**
   * @param {string} subrace - Name of the elven subrace
   */
  constructor(subrace) {
    const subraceData = ELVEN_SUBRACES[subrace];
    if (!subraceData) {
      throw new Error(`Invalid elf subrace: ${subrace}`);
    }

    const config = {
      name: subraceData.name,
      description: subraceData.description,
      attributeBonuses: {
        dexterity: 2,
        ...subraceData.attributeBonuses
      },
      traits: [...BASE_ELVEN_TRAITS, ...subraceData.traits],
      cultures: ELVEN_CULTURES,
      size: {
        category: "Medium",
        height: { min: 160, max: 190 },
        weight: { min: 45, max: 80 }
      },
      speed: subrace === 'wood' ? 35 : 30,
      languages: {
        known: ["Common", "Elvish"],
        bonus: subrace === 'high' ? 1 : 0
      }
    };

    super(config);
    this.subrace = subrace;
  }

  /**
   * Get subrace-specific features
   * @returns {ElvenSubrace}
   */
  getSubraceFeatures() {
    return ELVEN_SUBRACES[this.subrace];
  }
}